/*
 * The on-screen keyboard on phones: some browsers shrink the page for it (Android Chrome and Samsung Internet
 * with interactive-widget=resizes-content, sometimes iOS home-screen apps), others only shrink the visible part
 * and scroll the page up (iOS Safari). Either way the browser may scroll the page to show the focused field, so
 * the chat's header would slide up under the status bar and who the message is for would no longer be seen.
 * As native-feeling chat apps do, while the keyboard is open the app is fitted to the part of the screen that is
 * actually visible: --vv-height sizes it and --vv-top moves it (a transform, so following the browser's own
 * scrolling costs no layout). The app follows the browser instead of scrolling the page back: fighting iOS's
 * scroll-to-field made the screen shake while the keyboard slid in. The page is put back at the top once the
 * keyboard has gone.
 *
 * The keyboard is told by how much the visible height dropped below the tallest it has been at this width (the
 * page's own height can shrink with it, so comparing with window.innerHeight is not enough).
 */
const KEYBOARD_MIN_PX = 120;

function editable(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color'].includes(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

export function trackVisualViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;
  let frame = 0;
  // The tallest visible height seen at this width (with no keyboard); a new width (rotation) starts over.
  let tallest = 0;
  let width = 0;
  let shown = { open: false, height: -1, top: -1 };
  const update = () => {
    frame = 0;
    if (window.innerWidth !== width) {
      width = window.innerWidth;
      tallest = 0;
    }
    // Pinch zoom also shrinks the visible part: that is not the keyboard.
    const zoomed = vv.scale > 1.01;
    const typing = editable(document.activeElement);
    if (!zoomed) tallest = Math.max(tallest, vv.height, typing ? 0 : window.innerHeight);
    const open = !zoomed && typing && tallest - vv.height > KEYBOARD_MIN_PX;
    const height = Math.round(vv.height);
    // Where the visible part starts within the layout viewport (the browser moved it to show the field): the
    // fixed app is moved there.
    const top = open ? Math.round(vv.offsetTop) : 0;
    // Only what changed is written: no restyle on the many identical events of a keyboard animation.
    if (open !== shown.open) root.classList.toggle('keyboard-open', open);
    if (height !== shown.height) root.style.setProperty('--vv-height', `${height}px`);
    if (top !== shown.top) root.style.setProperty('--vv-top', `${top}px`);
    shown = { open, height, top };
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  // The keyboard's size is reported a moment after the field gets focus (and again as it finishes animating).
  window.addEventListener('focusin', () => {
    schedule();
    for (const ms of [100, 350, 700]) setTimeout(schedule, ms);
  });
  window.addEventListener('focusout', () => setTimeout(() => {
    schedule();
    if (!editable(document.activeElement) && window.scrollY !== 0) window.scrollTo(0, 0);
  }, 150));
  update();
}
